import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PhoneIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  QueueListIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import authService from '../services/auth.service';
import analysisService from '../services/analysis.service';
import { User } from '../types/auth.types';
import {
  AnalysisListItem,
  AnalysisStatus,
  AnalysisSearchFilters,
  SearchMetadata,
} from '../types/analysis.types';
import AuthenticatedLayout from '../components/AuthenticatedLayout';
import { formatDateTime, getTimezoneAbbreviation } from '../utils/dateFormat';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Parse URL search params to AnalysisSearchFilters object.
 * Handles boolean string conversion and validates filter values.
 */
function parseUrlParamsToFilters(searchParams: URLSearchParams): AnalysisSearchFilters {
  const filters: AnalysisSearchFilters = {};

  // Full-text search
  const q = searchParams.get('q');
  if (q) filters.q = q;

  // Compliance filters (boolean)
  const complianceIdentityVerified = searchParams.get('complianceIdentityVerified');
  if (complianceIdentityVerified !== null) {
    filters.complianceIdentityVerified = complianceIdentityVerified === 'true';
  }

  const complianceFlagged = searchParams.get('complianceFlagged');
  if (complianceFlagged !== null) {
    filters.complianceFlagged = complianceFlagged === 'true';
  }

  const complianceProhibitedPhrasesUsed = searchParams.get('complianceProhibitedPhrasesUsed');
  if (complianceProhibitedPhrasesUsed !== null) {
    filters.complianceProhibitedPhrasesUsed = complianceProhibitedPhrasesUsed === 'true';
  }

  const complianceSensitiveDataShared = searchParams.get('complianceSensitiveDataShared');
  if (complianceSensitiveDataShared !== null) {
    filters.complianceSensitiveDataShared = complianceSensitiveDataShared === 'true';
  }

  const complianceMandatoryDisclosuresMissing = searchParams.get('complianceMandatoryDisclosuresMissing');
  if (complianceMandatoryDisclosuresMissing !== null) {
    filters.complianceMandatoryDisclosuresMissing = complianceMandatoryDisclosuresMissing === 'true';
  }

  const compliancePurposeNotDeclared = searchParams.get('compliancePurposeNotDeclared');
  if (compliancePurposeNotDeclared !== null) {
    filters.compliancePurposeNotDeclared = compliancePurposeNotDeclared === 'true';
  }

  // Quality filters
  const qualitySentimentFlagged = searchParams.get('qualitySentimentFlagged');
  if (qualitySentimentFlagged !== null) {
    filters.qualitySentimentFlagged = qualitySentimentFlagged === 'true';
  }

  const qualityInterruptionsMin = searchParams.get('qualityInterruptionsMin');
  if (qualityInterruptionsMin !== null) {
    const parsed = parseInt(qualityInterruptionsMin, 10);
    if (!isNaN(parsed)) filters.qualityInterruptionsMin = parsed;
  }

  const qualityGreetingMissing = searchParams.get('qualityGreetingMissing');
  if (qualityGreetingMissing !== null) {
    filters.qualityGreetingMissing = qualityGreetingMissing === 'true';
  }

  const qualityObjectionsUnhandled = searchParams.get('qualityObjectionsUnhandled');
  if (qualityObjectionsUnhandled !== null) {
    filters.qualityObjectionsUnhandled = qualityObjectionsUnhandled === 'true';
  }

  // Coaching filters
  const coachingPriority = searchParams.get('coachingPriority');
  if (coachingPriority && ['high', 'medium', 'low'].includes(coachingPriority)) {
    filters.coachingPriority = coachingPriority as 'high' | 'medium' | 'low';
  }

  const coachingEffortScoreMin = searchParams.get('coachingEffortScoreMin');
  if (coachingEffortScoreMin !== null) {
    const parsed = parseFloat(coachingEffortScoreMin);
    if (!isNaN(parsed)) filters.coachingEffortScoreMin = parsed;
  }

  // Date range
  const dateFrom = searchParams.get('dateFrom');
  if (dateFrom) filters.dateFrom = dateFrom;

  const dateTo = searchParams.get('dateTo');
  if (dateTo) filters.dateTo = dateTo;

  return filters;
}

/**
 * Convert AnalysisSearchFilters to URLSearchParams.
 */
function filtersToUrlParams(filters: AnalysisSearchFilters, source?: string): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.q) params.set('q', filters.q);
  if (filters.complianceIdentityVerified !== undefined) {
    params.set('complianceIdentityVerified', String(filters.complianceIdentityVerified));
  }
  if (filters.complianceFlagged !== undefined) {
    params.set('complianceFlagged', String(filters.complianceFlagged));
  }
  if (filters.complianceProhibitedPhrasesUsed !== undefined) {
    params.set('complianceProhibitedPhrasesUsed', String(filters.complianceProhibitedPhrasesUsed));
  }
  if (filters.complianceSensitiveDataShared !== undefined) {
    params.set('complianceSensitiveDataShared', String(filters.complianceSensitiveDataShared));
  }
  if (filters.complianceMandatoryDisclosuresMissing !== undefined) {
    params.set('complianceMandatoryDisclosuresMissing', String(filters.complianceMandatoryDisclosuresMissing));
  }
  if (filters.compliancePurposeNotDeclared !== undefined) {
    params.set('compliancePurposeNotDeclared', String(filters.compliancePurposeNotDeclared));
  }
  if (filters.qualitySentimentFlagged !== undefined) {
    params.set('qualitySentimentFlagged', String(filters.qualitySentimentFlagged));
  }
  if (filters.qualityInterruptionsMin !== undefined) {
    params.set('qualityInterruptionsMin', String(filters.qualityInterruptionsMin));
  }
  if (filters.qualityGreetingMissing !== undefined) {
    params.set('qualityGreetingMissing', String(filters.qualityGreetingMissing));
  }
  if (filters.qualityObjectionsUnhandled !== undefined) {
    params.set('qualityObjectionsUnhandled', String(filters.qualityObjectionsUnhandled));
  }
  if (filters.coachingPriority) {
    params.set('coachingPriority', filters.coachingPriority);
  }
  if (filters.coachingEffortScoreMin !== undefined) {
    params.set('coachingEffortScoreMin', String(filters.coachingEffortScoreMin));
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);

  // Preserve source param if present
  if (source) params.set('source', source);

  return params;
}

const formatDuration = (ms: number | null): string => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Check if analysis has any compliance violations.
 * Returns true if any of the 5 compliance checks are flagged/failed.
 */
const hasComplianceViolation = (analysis: AnalysisListItem): boolean => {
  if (!analysis.results?.compliance) return false;
  const c = analysis.results.compliance;
  return (
    c.identity_verification?.flagged ||
    c.mandatory_disclosures?.read === false ||
    c.purpose_declaration?.flagged ||
    c.prohibited_phrases?.used ||
    c.sensitive_data?.shared
  );
};

/**
 * Get the highest coaching priority from analysis recommendations.
 * Returns 'high', 'medium', 'low', or null if no recommendations.
 */
const getCoachingPriority = (analysis: AnalysisListItem): 'high' | 'medium' | 'low' | null => {
  if (!analysis.results?.coaching?.recommendations?.length) return null;
  const priorities = analysis.results.coaching.recommendations.map(r => r.priority);
  if (priorities.includes('high')) return 'high';
  if (priorities.includes('medium')) return 'medium';
  return 'low';
};

const Analyses: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation('analyses');
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [analyses, setAnalyses] = useState<AnalysisListItem[]>([]);
  const [total, setTotal] = useState<number | null>(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<AnalysisStatus | ''>('');
  const [isSearching, setIsSearching] = useState(false);

  // Advanced filters - initialize from URL params
  const [showFilters, setShowFilters] = useState(false);
  const [searchMetadata, setSearchMetadata] = useState<SearchMetadata | null>(null);
  const [filters, setFilters] = useState<AnalysisSearchFilters>(() => parseUrlParamsToFilters(searchParams));
  const [isInitialized, setIsInitialized] = useState(false);

  // Track if user navigated from dashboard
  const source = searchParams.get('source');

  const PAGE_SIZE = 50;

  const formatDate = (timestamp: number): string => {
    return formatDateTime(timestamp, i18n.language);
  };

  // Get timezone abbreviation for display in column header
  const timezoneAbbr = getTimezoneAbbreviation();

  const getStatusIcon = (status: AnalysisStatus) => {
    switch (status) {
      case AnalysisStatus.QUEUED:
        return <QueueListIcon className="h-5 w-5 text-gray-500" />;
      case AnalysisStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case AnalysisStatus.PROCESSING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case AnalysisStatus.FAILED:
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusBadge = (status: AnalysisStatus) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case AnalysisStatus.QUEUED:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case AnalysisStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case AnalysisStatus.PROCESSING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case AnalysisStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
    }
  };

  // Render compliance violation pill (only for completed analyses with violations)
  const renderComplianceViolationPill = (analysis: AnalysisListItem) => {
    if (analysis.status !== AnalysisStatus.COMPLETED) return null;
    if (!hasComplianceViolation(analysis)) return null;

    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
        {t('analyses:indicators.violation')}
      </span>
    );
  };

  // Render coaching priority pill (only for high/medium priority)
  const renderCoachingPriorityPill = (analysis: AnalysisListItem) => {
    if (analysis.status !== AnalysisStatus.COMPLETED) return null;
    const priority = getCoachingPriority(analysis);
    if (!priority || priority === 'low') return null;

    const styles: Record<'high' | 'medium', string> = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-amber-100 text-amber-800',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[priority]}`}>
        {t(`analyses:indicators.priority.${priority}`)}
      </span>
    );
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  // Initialize from URL params on mount
  useEffect(() => {
    const initialFilters = parseUrlParamsToFilters(searchParams);
    const hasFilters = Object.keys(initialFilters).length > 0;

    if (hasFilters) {
      setFilters(initialFilters);
      setShowFilters(true); // Auto-expand filter panel when navigating with filters
    }
    setIsInitialized(true);
  }, []); // Only run once on mount

  // Sync filters to URL when they change (after initialization)
  useEffect(() => {
    if (!isInitialized) return;

    const newParams = filtersToUrlParams(filters, source || undefined);

    // Only update if params actually changed to avoid infinite loops
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [filters, isInitialized]);

  useEffect(() => {
    if (user) {
      fetchAnalyses();
    }
  }, [user, currentPage, statusFilter]);

  const fetchUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      navigate('/signin');
    }
  };

  const fetchAnalyses = async () => {
    setIsLoading(true);
    try {
      const response = await analysisService.listAnalyses({
        status: statusFilter || undefined,
        search: searchTerm || undefined,
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
        // Full-text search
        q: filters.q || undefined,
        // Compliance filters
        complianceIdentityVerified: filters.complianceIdentityVerified,
        complianceFlagged: filters.complianceFlagged,
        complianceProhibitedPhrasesUsed: filters.complianceProhibitedPhrasesUsed,
        complianceSensitiveDataShared: filters.complianceSensitiveDataShared,
        complianceMandatoryDisclosuresMissing: filters.complianceMandatoryDisclosuresMissing,
        compliancePurposeNotDeclared: filters.compliancePurposeNotDeclared,
        // Quality filters
        qualitySentimentFlagged: filters.qualitySentimentFlagged,
        qualityInterruptionsMin: filters.qualityInterruptionsMin,
        qualityGreetingMissing: filters.qualityGreetingMissing,
        qualityObjectionsUnhandled: filters.qualityObjectionsUnhandled,
        // Coaching filters
        coachingPriority: filters.coachingPriority,
        coachingEffortScoreMin: filters.coachingEffortScoreMin,
        // Date range
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });
      setAnalyses(response.data);
      setTotal(response.total);
      setHasMore(response.has_more);
      setSearchMetadata(response.search_metadata || null);
    } catch (error) {
      console.error('Failed to fetch analyses:', error);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setCurrentPage(1);
    fetchAnalyses();
  };

  const handleRowClick = (id: string) => {
    navigate(`/analyses/${id}`);
  };

  // Filter helpers
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.q) count++;
    if (filters.complianceIdentityVerified !== undefined) count++;
    if (filters.complianceFlagged !== undefined) count++;
    if (filters.complianceProhibitedPhrasesUsed !== undefined) count++;
    if (filters.complianceSensitiveDataShared !== undefined) count++;
    if (filters.complianceMandatoryDisclosuresMissing !== undefined) count++;
    if (filters.compliancePurposeNotDeclared !== undefined) count++;
    if (filters.qualitySentimentFlagged !== undefined) count++;
    if (filters.qualityInterruptionsMin !== undefined) count++;
    if (filters.qualityGreetingMissing !== undefined) count++;
    if (filters.qualityObjectionsUnhandled !== undefined) count++;
    if (filters.coachingPriority) count++;
    if (filters.coachingEffortScoreMin !== undefined) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    return count;
  };

  const clearFilter = (key: keyof AnalysisSearchFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({});
    setCurrentPage(1);
    // Clear source param and all filters from URL
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const updateFilter = <K extends keyof AnalysisSearchFilters>(
    key: K,
    value: AnalysisSearchFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Trigger search when filters change
  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        fetchAnalyses();
      }, 300); // Debounce for text inputs
      return () => clearTimeout(timer);
    }
  }, [filters]);

  // Calculate stats
  // When total is null (filtered queries), show current page count instead
  const displayTotal = total ?? analyses.length;
  const stats = {
    total: displayTotal,
    completed: analyses.filter(a => a.status === AnalysisStatus.COMPLETED).length,
    processing: analyses.filter(a => a.status === AnalysisStatus.PROCESSING).length,
    failed: analyses.filter(a => a.status === AnalysisStatus.FAILED).length,
  };

  // When total is null, we rely on hasMore for pagination
  const totalPages = total !== null ? Math.ceil(total / PAGE_SIZE) : (hasMore ? currentPage + 1 : currentPage);

  if (isLoading && analyses.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <span className="sr-only">{t('common:app.loading')}</span>
      </div>
    );
  }

  return (
    <AuthenticatedLayout>
      <header>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
              {t('analyses:title')}
            </h1>
          </div>
        </header>
        <main>
          <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
            {/* Filtered Results Banner - shows when navigating from dashboard */}
            {source === 'dashboard' && getActiveFilterCount() > 0 && (
              <div className="mt-6 rounded-md bg-indigo-50 border border-indigo-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FunnelIcon className="h-5 w-5 text-indigo-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-indigo-800">
                        {t('analyses:filters.filteredResults')}
                      </p>
                      <p className="text-sm text-indigo-600">
                        {t('analyses:filters.filteredFromDashboard', { count: getActiveFilterCount() })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <ArrowLeftIcon className="h-4 w-4 mr-1" />
                      {t('analyses:filters.backToDashboard')}
                    </button>
                    <button
                      onClick={clearAllFilters}
                      className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      {t('analyses:search.clearAll')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <PhoneIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('analyses:stats.totalCalls')}</dt>
                        <dd className="text-2xl font-semibold text-gray-900">{stats.total.toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('analyses:stats.completed')}</dt>
                        <dd className="text-2xl font-semibold text-gray-900">{stats.completed}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('analyses:stats.processing')}</dt>
                        <dd className="text-2xl font-semibold text-gray-900">{stats.processing}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ExclamationCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('analyses:stats.failed')}</dt>
                        <dd className="text-2xl font-semibold text-gray-900">{stats.failed}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mt-8 bg-white shadow rounded-lg p-6">
              {/* Search Row */}
              <form onSubmit={handleSearch} className="sm:flex sm:items-center sm:space-x-4">
                <div className="flex-1">
                  <label htmlFor="search" className="sr-only">
                    {t('analyses:search.transcriptSearch')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      name="search"
                      id="search"
                      className="block w-full rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder={t('analyses:search.transcriptPlaceholder')}
                      value={filters.q || ''}
                      onChange={(e) => updateFilter('q', e.target.value || undefined)}
                    />
                  </div>
                </div>

                <div className="mt-3 sm:mt-0">
                  <label htmlFor="status-filter" className="sr-only">
                    {t('analyses:search.filterByStatus')}
                  </label>
                  <select
                    id="status-filter"
                    name="status-filter"
                    className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as AnalysisStatus | '');
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">{t('analyses:search.allStatuses')}</option>
                    <option value={AnalysisStatus.COMPLETED}>{t('analyses:status.completed')}</option>
                    <option value={AnalysisStatus.PROCESSING}>{t('analyses:status.processing')}</option>
                    <option value={AnalysisStatus.FAILED}>{t('analyses:status.failed')}</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={classNames(
                    'mt-3 sm:mt-0 inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                    getActiveFilterCount() > 0
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <FunnelIcon className="h-5 w-5 mr-2" />
                  {t('analyses:search.filters')}
                  {getActiveFilterCount() > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-indigo-600 text-white">
                      {getActiveFilterCount()}
                    </span>
                  )}
                  {showFilters ? (
                    <ChevronUpIcon className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 ml-2" />
                  )}
                </button>

                <button
                  type="submit"
                  disabled={isSearching}
                  className="mt-3 sm:mt-0 inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-indigo-400"
                >
                  {isSearching ? (
                    <>
                      <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                      {t('analyses:search.searching')}
                    </>
                  ) : (
                    t('analyses:search.button')
                  )}
                </button>
              </form>

              {/* Active Filters Chips */}
              {getActiveFilterCount() > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-500">{t('analyses:search.activeFilters')}:</span>
                  {filters.q && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-800">
                      {t('analyses:filters.search')}: "{filters.q}"
                      <button onClick={() => clearFilter('q')} className="hover:text-indigo-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {filters.complianceFlagged !== undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">
                      {t('analyses:filters.complianceFlagged')}
                      <button onClick={() => clearFilter('complianceFlagged')} className="hover:text-red-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {filters.complianceIdentityVerified !== undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800">
                      {filters.complianceIdentityVerified ? t('analyses:filters.identityVerified') : t('analyses:filters.identityNotVerified')}
                      <button onClick={() => clearFilter('complianceIdentityVerified')} className="hover:text-yellow-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {filters.qualitySentimentFlagged !== undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-3 py-1 text-sm font-medium text-orange-800">
                      {t('analyses:filters.sentimentFlagged')}
                      <button onClick={() => clearFilter('qualitySentimentFlagged')} className="hover:text-orange-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {filters.complianceMandatoryDisclosuresMissing !== undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">
                      {t('analyses:filters.mandatoryDisclosuresMissing')}
                      <button onClick={() => clearFilter('complianceMandatoryDisclosuresMissing')} className="hover:text-red-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {filters.compliancePurposeNotDeclared !== undefined && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">
                      {t('analyses:filters.purposeNotDeclared')}
                      <button onClick={() => clearFilter('compliancePurposeNotDeclared')} className="hover:text-red-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {filters.coachingPriority && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800">
                      {t('analyses:filters.priority')}: {filters.coachingPriority}
                      <button onClick={() => clearFilter('coachingPriority')} className="hover:text-purple-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {(filters.dateFrom || filters.dateTo) && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
                      {t('analyses:filters.dateRange')}: {filters.dateFrom || '...'} - {filters.dateTo || '...'}
                      <button onClick={() => { clearFilter('dateFrom'); clearFilter('dateTo'); }} className="hover:text-blue-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    {t('analyses:search.clearAll')}
                  </button>
                </div>
              )}

              {/* Collapsible Filter Panel */}
              {showFilters && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Compliance Filters */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">{t('analyses:filters.compliance')}</h4>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.complianceFlagged === true}
                            onChange={(e) => updateFilter('complianceFlagged', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.anyComplianceIssue')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.complianceIdentityVerified === false}
                            onChange={(e) => updateFilter('complianceIdentityVerified', e.target.checked ? false : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.identityNotVerified')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.complianceProhibitedPhrasesUsed === true}
                            onChange={(e) => updateFilter('complianceProhibitedPhrasesUsed', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.prohibitedPhrases')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.complianceSensitiveDataShared === true}
                            onChange={(e) => updateFilter('complianceSensitiveDataShared', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.sensitiveDataShared')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.complianceMandatoryDisclosuresMissing === true}
                            onChange={(e) => updateFilter('complianceMandatoryDisclosuresMissing', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.mandatoryDisclosuresMissing')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.compliancePurposeNotDeclared === true}
                            onChange={(e) => updateFilter('compliancePurposeNotDeclared', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.purposeNotDeclared')}</span>
                        </label>
                      </div>
                    </div>

                    {/* Quality Filters */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">{t('analyses:filters.quality')}</h4>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.qualitySentimentFlagged === true}
                            onChange={(e) => updateFilter('qualitySentimentFlagged', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.sentimentDrop')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.qualityGreetingMissing === true}
                            onChange={(e) => updateFilter('qualityGreetingMissing', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.greetingMissing')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.qualityObjectionsUnhandled === true}
                            onChange={(e) => updateFilter('qualityObjectionsUnhandled', e.target.checked ? true : undefined)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('analyses:filters.objectionsUnhandled')}</span>
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-700">{t('analyses:filters.interruptions')} &ge;</span>
                          <input
                            type="number"
                            min="0"
                            value={filters.qualityInterruptionsMin ?? ''}
                            onChange={(e) => updateFilter('qualityInterruptionsMin', e.target.value ? parseInt(e.target.value) : undefined)}
                            className="w-16 rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="5"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Coaching Filters */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">{t('analyses:filters.coaching')}</h4>
                      <div className="space-y-2">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">{t('analyses:filters.priority')}</label>
                          <select
                            value={filters.coachingPriority || ''}
                            onChange={(e) => updateFilter('coachingPriority', e.target.value as 'high' | 'medium' | 'low' | undefined || undefined)}
                            className="block w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            <option value="">{t('analyses:filters.anyPriority')}</option>
                            <option value="high">{t('analyses:filters.priorityHigh')}</option>
                            <option value="medium">{t('analyses:filters.priorityMedium')}</option>
                            <option value="low">{t('analyses:filters.priorityLow')}</option>
                          </select>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-700">{t('analyses:filters.effortScore')} &ge;</span>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            value={filters.coachingEffortScoreMin ?? ''}
                            onChange={(e) => updateFilter('coachingEffortScoreMin', e.target.value ? parseFloat(e.target.value) : undefined)}
                            className="w-20 rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="7.0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Date Range */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">{t('analyses:filters.dateRange')}</h4>
                      <div className="space-y-2">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">{t('analyses:filters.from')}</label>
                          <input
                            type="date"
                            value={filters.dateFrom || ''}
                            onChange={(e) => updateFilter('dateFrom', e.target.value || undefined)}
                            className="block w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">{t('analyses:filters.to')}</label>
                          <input
                            type="date"
                            value={filters.dateTo || ''}
                            onChange={(e) => updateFilter('dateTo', e.target.value || undefined)}
                            className="block w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Metadata */}
              {searchMetadata && searchMetadata.filters_applied.length > 0 && (
                <div className="mt-4 text-sm text-gray-500">
                  {t('analyses:search.searchTime', { ms: searchMetadata.search_time_ms })}
                  {searchMetadata.query && ` • ${t('analyses:search.searchQuery')}: "${searchMetadata.query}"`}
                </div>
              )}
            </div>

            {/* Results Table */}
            <div className="mt-8 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            {t('analyses:table.status')}
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t('analyses:table.indicators')}
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t('analyses:table.agentId')}
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t('analyses:table.contactId')}
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t('analyses:table.duration')}
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            {t('analyses:table.created')}
                            <span className="ml-1 text-xs font-normal text-gray-500">({timezoneAbbr})</span>
                          </th>
                          <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">{t('analyses:table.view')}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {isLoading ? (
                          <tr>
                            <td colSpan={7} className="px-3 py-8 text-center">
                              <div className="flex justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                              </div>
                            </td>
                          </tr>
                        ) : analyses.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                              {t('analyses:table.noResults')} {searchTerm && t('analyses:table.adjustSearch')}
                            </td>
                          </tr>
                        ) : (
                          analyses.map((analysis) => (
                            <tr
                              key={analysis.id}
                              onClick={() => handleRowClick(analysis.id)}
                              className="hover:bg-gray-50 cursor-pointer"
                            >
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                                <span className={getStatusBadge(analysis.status)}>
                                  {t(`analyses:status.${analysis.status.toLowerCase()}`)}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                <div className="flex flex-wrap gap-1">
                                  {renderComplianceViolationPill(analysis)}
                                  {renderCoachingPriorityPill(analysis)}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                <div className="font-medium">
                                  {analysis.call.agent_id === 'unknown'
                                    ? t('analyses:table.unknown')
                                    : analysis.call.agent_id}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                {analysis.call.contact_id}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDuration(analysis.call.duration_ms)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDate(analysis.created)}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick(analysis.id);
                                  }}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  {t('analyses:table.view')}<span className="sr-only">, {analysis.id}</span>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {t('analyses:pagination.previous')}
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={!hasMore}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {t('analyses:pagination.next')}
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      {t('analyses:pagination.showing')} <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span> {t('analyses:pagination.to')}{' '}
                      <span className="font-medium">
                        {total !== null ? Math.min(currentPage * PAGE_SIZE, total) : (currentPage - 1) * PAGE_SIZE + analyses.length}
                      </span>{' '}
                      {total !== null ? (
                        <>{t('analyses:pagination.of')} <span className="font-medium">{total}</span> {t('analyses:pagination.results')}</>
                      ) : (
                        <>{t('analyses:pagination.results')}{hasMore && '+'}</>
                      )}
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">{t('analyses:pagination.previous')}</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                        const pageNum = currentPage <= 3 ? idx + 1 : currentPage + idx - 2;
                        if (pageNum > totalPages) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={classNames(
                              pageNum === currentPage
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                              'relative inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20'
                            )}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setCurrentPage(p => p + 1)}
                        disabled={!hasMore}
                        className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">{t('analyses:pagination.next')}</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
    </AuthenticatedLayout>
  );
};

export default Analyses;
